import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import FeeBreakdown from '../components/FeeBreakdown';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payments = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const { feeRates, refreshFeeRates } = useFees();
  const clientFeeRate = feeRates.client_fee_rate / 100;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentData();
    refreshFeeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentData = async () => {
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data || []);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  // All bookings that have been paid
  const paidBookings = bookings.filter(b => b.payment_status === 'paid');
  // For Release: paid but not completed
  const forReleaseBookings = paidBookings.filter(b => b.status !== 'completed');
  // Completed: paid and completed
  const completedBookings = paidBookings.filter(b => b.status === 'completed');

  // Calculate totals
  const totalForRelease = forReleaseBookings.reduce((sum, b) => {
    const svc = b.final_amount || b.price || 0;
    const fee = b.client_fee || svc * clientFeeRate;
    return sum + svc + fee;
  }, 0);

  const totalSpent = completedBookings.reduce((sum, b) => {
    const svc = b.final_amount || b.price || 0;
    const fee = b.client_fee || svc * clientFeeRate;
    return sum + svc + fee;
  }, 0);

  const totalServiceAmount = paidBookings.reduce((sum, b) => sum + (b.final_amount || b.price || 0), 0);
  const totalPlatformFees = paidBookings.reduce((sum, b) => {
    const svc = b.final_amount || b.price || 0;
    return sum + (b.client_fee || svc * clientFeeRate);
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">{t('payments.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payments-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent" data-testid="page-title">
          {t('payments.title')}
        </h1>

        {/* Spending Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="spending-summary">
          <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-purple-500">
            <h3 className="text-gray-500 text-sm font-medium">{t('payments.forRelease')}</h3>
            <p className="text-2xl font-bold text-purple-600 mt-2" data-testid="for-release-total">
              PHP {totalForRelease.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.inEscrow')}</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-blue-500">
            <h3 className="text-gray-500 text-sm font-medium">{t('payments.totalSpent')}</h3>
            <p className="text-2xl font-bold text-blue-600 mt-2" data-testid="total-spent">
              PHP {totalSpent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.releasedAfter')}</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-green-500">
            <h3 className="text-gray-500 text-sm font-medium">{t('payments.servicesPurchased')}</h3>
            <p className="text-2xl font-bold text-green-600 mt-2">
              PHP {totalServiceAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.serviceAmountsOnly')}</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-orange-500">
            <h3 className="text-gray-500 text-sm font-medium">{t('payments.platformFeesPaid')}</h3>
            <p className="text-2xl font-bold text-orange-600 mt-2">
              PHP {totalPlatformFees.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.serviceFee', { rate: feeRates.client_fee_rate })}</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-teal-500">
            <h3 className="text-gray-500 text-sm font-medium">{t('payments.completedPayments')}</h3>
            <p className="text-2xl font-bold text-teal-600 mt-2">
              {completedBookings.length}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('payments.paidReleased')}</p>
          </div>
        </div>

        {/* Fee Info */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg mb-8 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="bg-white p-3 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">{t('payments.platformServiceFee')}</h3>
              <p className="text-gray-700">
                {t('payments.feeInfoLead')} <strong>{t('payments.feeBold', { rate: feeRates.client_fee_rate })}</strong> {t('payments.feeInfoRest')}
              </p>
              <FeeBreakdown rate={feeRates.client_fee_rate} />
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-testid="transactions-section">
          <h2 className="text-xl font-bold mb-6">{t('payments.paymentHistory')}</h2>
          {paidBookings.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <p className="text-gray-500 mb-4">{t('payments.noPayments')}</p>
              <Link to="/services" className="text-blue-600 hover:underline font-medium">
                {t('payments.browseServices')}
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.service')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.provider')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.serviceAmount')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.platformFee')} ({feeRates.client_fee_rate}%)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.totalPaid')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paidBookings.map((booking) => {
                    const serviceAmount = booking.final_amount || booking.price || 0;
                    const platformFee = booking.client_fee || serviceAmount * clientFeeRate;
                    const totalPaid = serviceAmount + platformFee;
                    const isForRelease = booking.status !== 'completed';
                    const paymentDate = booking.scheduled_date || booking.date || booking.created_at;

                    return (
                      <tr key={booking.id} data-testid={`payment-row-${booking.id}`}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(paymentDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {booking.service_title}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {booking.provider_name}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          PHP {serviceAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-600">
                          +PHP {platformFee.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                          PHP {totalPaid.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            isForRelease
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {isForRelease ? t('payments.statusForRelease') : t('payments.statusCompleted')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Payments;
